import { useState } from "react";
import {
	AlertCircle,
	Filter,
	RefreshCw,
	RotateCcw,
	Trash2,
	X,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@workspace/ui/components/card";
import { Checkbox } from "@workspace/ui/components/checkbox";
import { Input } from "@workspace/ui/components/input";
import { Label } from "@workspace/ui/components/label";
import { TimeAgo } from "@workspace/ui/components/time-ago";

import { devtoolsApi } from "./api";

export interface Message {
	id: string;
	executionTime: string;
	body: Record<string, unknown>;
	messageType: string;
	receivedAt: string;
	source: string;
	exceptionType: string;
	exceptionMessage: string;
	sentAt: Date;
	replayable: boolean;
}

export interface DeadLetterFilters {
	limit?: number;
	messageType?: string;
	exceptionType?: string;
	exceptionMessage?: string;
	from?: string;
	until?: string;
	tenantId?: string;
}

export function DeadLetterPanelContent({
	messages,
	lastCalledAt,
	hasNextPage,
	onNextPage,
	filters = {},
	onFiltersChange,
	onRefetch,
}: {
	messages: Message[];
	lastCalledAt?: string;
	hasNextPage: boolean;
	onNextPage: () => void;
	filters?: DeadLetterFilters;
	onFiltersChange?: (filters: DeadLetterFilters) => void;
	onRefetch?: () => Promise<void>;
}) {
	const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
	const [showFilters, setShowFilters] = useState(false);
	const [localFilters, setLocalFilters] = useState<DeadLetterFilters>(filters);
	const [isFetching, setIsFetching] = useState(false);

	const queryClient = useQueryClient();
	// HA-769 / D-HA-24: replay and delete now reject on a refusal, so say so rather than failing
	// silently. The selection is kept, so the same button retries.
	const [actionError, setActionError] = useState<string | null>(null);

	const replayMutation = useMutation({
		mutationFn: (messageIds: string[]) =>
			devtoolsApi.replayDeadLetters(messageIds),
		onMutate: () => setActionError(null),
		onError: (error: Error) => {
			setActionError(
				`Couldn't replay the selected messages: ${error.message || "the server refused"}`,
			);
		},
		onSuccess: () => {
			setSelectedIds(new Set());
			queryClient.invalidateQueries({
				queryKey: ["devtools", "dead-letters"],
			});
		},
	});

	const deleteMutation = useMutation({
		mutationFn: (messageIds: string[]) =>
			devtoolsApi.deleteDeadLetters(messageIds),
		onMutate: () => setActionError(null),
		onError: (error: Error) => {
			setActionError(
				`Couldn't delete the selected messages: ${error.message || "the server refused"}`,
			);
		},
		onSuccess: () => {
			setSelectedIds(new Set());
			queryClient.invalidateQueries({
				queryKey: ["devtools", "dead-letters"],
			});
		},
	});

	const toggleSelection = (id: string) => {
		const newSelected = new Set(selectedIds);
		if (newSelected.has(id)) {
			newSelected.delete(id);
		} else {
			newSelected.add(id);
		}
		setSelectedIds(newSelected);
	};

	const toggleSelectAll = () => {
		if (selectedIds.size === messages.length) {
			setSelectedIds(new Set());
		} else {
			setSelectedIds(new Set(messages.map((m) => m.id)));
		}
	};

	const handleReplay = async () => {
		if (selectedIds.size === 0) return;
		await replayMutation
			.mutateAsync(Array.from(selectedIds))
			.catch(() => undefined);
	};

	const handleDelete = async () => {
		if (selectedIds.size === 0) return;
		await deleteMutation
			.mutateAsync(Array.from(selectedIds))
			.catch(() => undefined);
	};

	const replayableSelected = Array.from(selectedIds).every((id) => {
		const message = messages.find((m) => m.id === id);
		return message?.replayable;
	});

	const updateFilter = (
		key: keyof DeadLetterFilters,
		value: string | number | undefined,
	) => {
		const newFilters = { ...localFilters, [key]: value || undefined };
		setLocalFilters(newFilters);
	};

	const applyFilters = () => {
		if (onFiltersChange) {
			onFiltersChange(localFilters);
		}
	};

	const clearFilters = () => {
		const emptyFilters: DeadLetterFilters = {};
		setLocalFilters(emptyFilters);
		if (onFiltersChange) {
			onFiltersChange(emptyFilters);
		}
	};

	const handleManualFetch = async () => {
		if (onRefetch) {
			setIsFetching(true);
			try {
				await onRefetch();
			} finally {
				setIsFetching(false);
			}
		}
	};

	const hasActiveFilters = Object.values(localFilters).some(
		(value) => value !== undefined && value !== "",
	);
	return (
		<div className="p-4">
			<Card className="w-full">
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							<CardTitle className="flex items-center gap-2">
								<AlertCircle className="size-5 text-destructive" />
								Dead Letter Queue
							</CardTitle>
							<CardDescription>
								{messages.length} failed{" "}
								{messages.length === 1 ? "message" : "messages"}
								{selectedIds.size > 0 && ` • ${selectedIds.size} selected`}
								{lastCalledAt && (
									<div className="mt-1 text-xs text-muted-foreground">
										Last called: <TimeAgo date={lastCalledAt} />
									</div>
								)}
							</CardDescription>
						</div>
						<div className="flex gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={() => setShowFilters(!showFilters)}
								className={hasActiveFilters ? "border-primary" : ""}
							>
								<Filter className="size-4" />
								Filters
								{hasActiveFilters && (
									<Badge variant="secondary" className="ml-2 h-5 px-1 text-xs">
										{Object.values(localFilters).filter((v) => v).length}
									</Badge>
								)}
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={handleManualFetch}
								disabled={isFetching}
							>
								<RefreshCw className="size-4" />
								{isFetching ? "Fetching..." : "Fetch"}
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={handleReplay}
								disabled={
									selectedIds.size === 0 ||
									!replayableSelected ||
									replayMutation.isPending
								}
							>
								<RotateCcw className="size-4" />
								{replayMutation.isPending ? "Replaying..." : "Replay"}
							</Button>
							<Button
								variant="destructive"
								size="sm"
								onClick={handleDelete}
								disabled={selectedIds.size === 0 || deleteMutation.isPending}
							>
								<Trash2 className="size-4" />
								{deleteMutation.isPending ? "Deleting..." : "Delete"}
							</Button>
						</div>
					</div>
					{actionError && (
						<p role="alert" className="mt-2 text-sm text-destructive">
							{actionError}
						</p>
					)}
					{showFilters && (
						<div className="mt-4 space-y-4 rounded-lg border bg-muted/50 p-4">
							<div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
								<div className="space-y-2">
									<Label htmlFor="limit" className="text-xs">
										Limit
									</Label>
									<Input
										id="limit"
										type="number"
										placeholder="e.g., 50"
										value={localFilters.limit || ""}
										onChange={(e) =>
											updateFilter(
												"limit",
												e.target.value
													? Number.parseInt(e.target.value)
													: undefined,
											)
										}
										className="h-9"
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="messageType" className="text-xs">
										Message Type
									</Label>
									<Input
										id="messageType"
										placeholder="e.g., OrderCreated"
										value={localFilters.messageType || ""}
										onChange={(e) =>
											updateFilter("messageType", e.target.value)
										}
										className="h-9"
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="exceptionType" className="text-xs">
										Exception Type
									</Label>
									<Input
										id="exceptionType"
										placeholder="e.g., NullReferenceException"
										value={localFilters.exceptionType || ""}
										onChange={(e) =>
											updateFilter("exceptionType", e.target.value)
										}
										className="h-9"
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="exceptionMessage" className="text-xs">
										Exception Message
									</Label>
									<Input
										id="exceptionMessage"
										placeholder="Search exception message"
										value={localFilters.exceptionMessage || ""}
										onChange={(e) =>
											updateFilter("exceptionMessage", e.target.value)
										}
										className="h-9"
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="from" className="text-xs">
										From Date
									</Label>
									<Input
										id="from"
										type="datetime-local"
										value={localFilters.from || ""}
										onChange={(e) => updateFilter("from", e.target.value)}
										className="h-9"
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="until" className="text-xs">
										Until Date
									</Label>
									<Input
										id="until"
										type="datetime-local"
										value={localFilters.until || ""}
										onChange={(e) => updateFilter("until", e.target.value)}
										className="h-9"
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="tenantId" className="text-xs">
										Tenant ID
									</Label>
									<Input
										id="tenantId"
										placeholder="Tenant identifier"
										value={localFilters.tenantId || ""}
										onChange={(e) => updateFilter("tenantId", e.target.value)}
										className="h-9"
									/>
								</div>
							</div>
							<div className="flex gap-2">
								<Button size="sm" onClick={applyFilters}>
									Apply Filters
								</Button>
								<Button
									size="sm"
									variant="outline"
									onClick={clearFilters}
									disabled={!hasActiveFilters}
								>
									<X className="size-4" />
									Clear All
								</Button>
							</div>
						</div>
					)}
				</CardHeader>
				<CardContent>
					{messages.length === 0 ? (
						<div className="py-8 text-center text-muted-foreground">
							No messages in dead letter queue
						</div>
					) : (
						<div className="space-y-4">
							<div className="flex items-center gap-2 border-b pb-2">
								<Checkbox
									checked={
										selectedIds.size === messages.length && messages.length > 0
									}
									onCheckedChange={toggleSelectAll}
								/>
								<span className="text-sm font-medium">Select All</span>
							</div>
							<div className="space-y-2">
								{messages.map((message) => (
									<div
										key={message.id}
										className="flex items-start gap-3 rounded-lg border p-4 transition-colors hover:bg-accent/50"
									>
										<Checkbox
											checked={selectedIds.has(message.id)}
											onCheckedChange={() => toggleSelection(message.id)}
											className="mt-1"
										/>
										<div className="flex-1 space-y-2">
											<div className="flex items-start justify-between gap-4">
												<div className="space-y-1">
													<div className="flex items-center gap-2">
														<span className="font-mono text-sm font-medium">
															{message.messageType}
														</span>
														{!message.replayable && (
															<Badge variant="secondary" className="text-xs">
																Non-replayable
															</Badge>
														)}
													</div>
													<div className="text-xs text-muted-foreground">
														<span className="font-medium">Source:</span>{" "}
														{message.source}
													</div>
												</div>
												<div className="space-y-1 text-right text-xs text-muted-foreground">
													<div
														title={new Date(message.sentAt).toLocaleString()}
													>
														<span className="font-medium">Sent:</span>{" "}
														<TimeAgo date={message.sentAt} />
													</div>
													<div>
														<span className="font-medium">Execution:</span>{" "}
														{message.executionTime}
													</div>
													<div>{message.receivedAt}</div>
												</div>
											</div>
											<div className="space-y-1">
												<div className="text-sm">
													<span className="font-medium text-destructive">
														{message.exceptionType}
													</span>
												</div>
												<div className="text-sm text-muted-foreground">
													{message.exceptionMessage}
												</div>
											</div>
											<details className="text-xs">
												<summary className="cursor-pointer text-muted-foreground hover:text-foreground">
													View message body
												</summary>
												<pre className="mt-2 overflow-x-auto rounded bg-muted p-2 text-xs">
													{JSON.stringify(message.body, null, 2)}
												</pre>
											</details>
										</div>
									</div>
								))}
							</div>

							{hasNextPage && (
								<div className="flex items-center justify-between border-t pt-4">
									<Button
										variant="outline"
										className="w-full"
										onClick={onNextPage}
									>
										More
									</Button>
								</div>
							)}
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
